import { useEffect, useState } from "react";
import { api, clock } from "../api";
import { appApi, country, date, route, SearchResult } from "./api";
import { useApp } from "./context";
import { EpisodeGrid, PageHeader, SectionHead } from "./Discover";
import { NoteList } from "./Episode";
import { PlayButton, usePlayer } from "./player";
import { Artwork, Empty, Icon, LanguageSelect } from "./visual";
import { AppearanceControls } from "./Appearance";
import { layouts } from "./preferences";
export function ThemeChooser() {
  const { theme, preference, t } = useApp();
  return (
    <div className="theme-chooser">
      {layouts.map((key, i) => (
        <button
          key={key}
          aria-pressed={theme === key}
          onClick={() => preference({ theme: key })}
        >
          <div className={"theme-preview preview-" + key}>
            <i />
            <div>
              <b />
              <span />
              <span />
              <span />
            </div>
          </div>
          <strong>
            <span>0{i + 1}</span>
            {key.charAt(0).toUpperCase() + key.slice(1)}
            {theme === key && <Icon name="check" size={16} />}
          </strong>
          <small>{t(key)}</small>
        </button>
      ))}
    </div>
  );
}
export function Account() {
  const { user, authReady, login, logout, t } = useApp();
  const [mode, setMode] = useState("login"),
    [name, setName] = useState(""),
    [password, setPassword] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  return (
    <>
      <PageHeader
        eyebrow={t("account")}
        title={user ? `${t("settings")}, ${user.username}.` : t("signin")}
        description={t("authIntro")}
      />
      <div className="account-layout">
        <section className="account-card">
          {!authReady ? (
            <Empty>{t("loading")}</Empty>
          ) : user ? (
            <>
              <div className="account-avatar">
                {user.username.slice(0, 2).toUpperCase()}
              </div>
              <h2>{user.username}</h2>
              <p>{t("noteHint")}</p>
              <button className="button" onClick={logout}>
                {t("signout")}
              </button>
            </>
          ) : (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setError("");
                setBusy(true);
                try {
                  await login(mode, name, password);
                  setPassword("");
                } catch (err) {
                  setError((err as Error).message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              <h2>{t(mode === "login" ? "signin" : "createAccount")}</h2>
              <label>
                {t("username")}
                <input
                  name="username"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="username"
                  minLength={3}
                  maxLength={32}
                  pattern="[a-zA-Z0-9_.\-]+"
                  required
                  autoCapitalize="none"
                  spellCheck={false}
                />
              </label>
              <label>
                {t("password")}
                <input
                  name="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={
                    mode === "login" ? "current-password" : "new-password"
                  }
                  minLength={8}
                  maxLength={128}
                  required
                />
              </label>
              {error && (
                <p className="error" role="alert">
                  {error}
                </p>
              )}
              <button className="button primary" disabled={busy}>
                {busy
                  ? t("loading")
                  : t(mode === "login" ? "signin" : "createAccount")}
              </button>
              <button
                className="text-button auth-switch"
                type="button"
                onClick={() => {
                  setMode(mode === "login" ? "register" : "login");
                  setError("");
                }}
              >
                {t(mode === "login" ? "createAccount" : "signin")}
                <Icon name="arrow" size={16} />
              </button>
            </form>
          )}
        </section>
        <section className="preferences-card">
          <SectionHead label={t("language")} />
          <p>{t("languageHint")}</p>
          <LanguageSelect />
          <SectionHead label={t("appearance")} />
          <AppearanceControls />
        </section>
      </div>
    </>
  );
}
export function Library() {
  const { user, t } = useApp();
  return (
    <>
      <PageHeader
        eyebrow={t("library")}
        title={t("notes")}
        description={t("noteHint")}
      />
      {user ? (
        <NoteList />
      ) : (
        <Empty>
          <a href="#/account">{t("noteLogin")}</a>
        </Empty>
      )}
    </>
  );
}
export function Directory({
  kind,
}: {
  kind: "shows" | "stations" | "artists";
}) {
  const { catalogue, t, lang } = useApp();
  const [q, setQ] = useState(""),
    [count, setCount] = useState(24);
  const items = catalogue[kind].filter((item) =>
    item.name.toLocaleLowerCase().includes(q.toLocaleLowerCase()),
  );
  useEffect(() => {
    setQ("");
    setCount(24);
  }, [kind]);
  return (
    <>
      <PageHeader
        eyebrow={t(kind)}
        title={t(
          kind === "shows"
            ? "showsTitle"
            : kind === "stations"
              ? "stationsTitle"
              : "artistsTitle",
        )}
      />
      <div className="directory-tools">
        <label className="search-field">
          <Icon name="search" />
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setCount(24);
            }}
            placeholder={t("search") + " " + t(kind).toLowerCase()}
            aria-label={t("search")}
          />
        </label>
        <span className="mono">
          {items.length} {t("results")}
        </span>
      </div>
      <div className={"directory-grid " + kind}>
        {items.slice(0, count).map((item, i) => {
          const ep = catalogue.episodes.find((e) =>
            item.episode_ids.includes(e.id),
          );
          return (
            <a
              className="directory-card"
              href={route(kind.slice(0, -1), item.id)}
              key={item.id}
            >
              <div className="directory-art">
                <Artwork seed={item.id} label={item.name} />
                {kind === "artists" && (
                  <span className="artist-monogram">
                    {item.name
                      .split(" ")
                      .map((s) => s[0])
                      .slice(0, 2)
                      .join("")}
                  </span>
                )}
              </div>
              <div>
                <span className="eyebrow">
                  {String(i + 1).padStart(2, "0")} /{" "}
                  {kind === "stations" && "country" in item
                    ? country(item.country, lang)
                    : kind === "shows" && "station_name" in item
                      ? item.station_name
                      : t(kind)}
                </span>
                <h2 dir="auto">{item.name}</h2>
                <p>
                  {item.episode_ids.length} {t("episodes")}
                  {kind === "shows" && ep
                    ? " · " +
                      ep.languages.map((l) => l.toUpperCase()).join(" / ")
                    : ""}
                </p>
              </div>
              <Icon name="arrow" />
            </a>
          );
        })}
      </div>
      {!items.length && <Empty>{t("noResults")}</Empty>}
      {items.length > count && (
        <button
          className="button load-more"
          onClick={() => setCount((n) => n + 24)}
        >
          {t("more")}
        </button>
      )}
    </>
  );
}
export function Entity({
  kind,
  id,
}: {
  kind: "show" | "station" | "artist";
  id: string;
}) {
  const { catalogue, t, lang, title } = useApp(),
    p = usePlayer();
  const entity = (
    kind === "show"
      ? catalogue.shows
      : kind === "station"
        ? catalogue.stations
        : catalogue.artists
  ).find((x) => x.id === id);
  if (!entity) return <Empty>{t("noResults")}</Empty>;
  const episodes = catalogue.episodes.filter((ep) =>
    entity.episode_ids.includes(ep.id),
  );
  const appearances = "appearances" in entity ? entity.appearances : [];
  return (
    <>
      <div className="entity-hero">
        <div className="entity-art">
          <Artwork seed={id} label={entity.name} />
        </div>
        <div>
          <span className="eyebrow">
            {t(
              kind === "show"
                ? "shows"
                : kind === "station"
                  ? "stations"
                  : "artists",
            )}
            {"country" in entity ? " / " + country(entity.country, lang) : ""}
          </span>
          <h1 dir="auto">{entity.name}</h1>
          {"station_name" in entity && (
            <a
              className="show-credit"
              href={route("station", entity.station_id)}
            >
              {entity.station_name}
              <Icon name="arrow" size={18} />
            </a>
          )}
          <p>
            {episodes.length} {t("episodes")} ·{" "}
            {clock(
              episodes.reduce((n, e) => n + (e.recording?.duration_ms || 0), 0),
            )}
          </p>
          {episodes[0] && <PlayButton episode={episodes[0]} large />}
        </div>
      </div>
      {kind === "artist" && (
        <section className="appearances">
          <SectionHead label={t("appeared")} />
          {appearances.map((a, i) => {
            const ep = episodes.find((e) => e.id === a.episode_id);
            return ep ? (
              <div className="appearance" key={i}>
                <span className="mono">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <a href={route("episode", ep.id, a.start_ms)}>
                    <strong>{a.title || title(ep)}</strong>
                  </a>
                  <p>
                    {ep.show_title} · {ep.station_name}
                  </p>
                  <span className="evidence-label">
                    {a.role === "guest"
                      ? t("guest")
                      : a.status === "candidate"
                        ? t("candidate")
                        : a.status === "publisher_only"
                          ? t("publisher")
                          : t("confirmed")}
                  </span>
                </div>
                {a.start_ms !== null ? (
                  <button
                    className="button"
                    onClick={() => {
                      p.load(ep, a.start_ms!);
                      location.hash = route("episode", ep.id, a.start_ms);
                    }}
                  >
                    <Icon name="play" size={16} />
                    {clock(a.start_ms)}
                  </button>
                ) : (
                  <a className="text-link" href={route("episode", ep.id)}>
                    {t("episode")}
                    <Icon name="arrow" size={16} />
                  </a>
                )}
              </div>
            ) : null;
          })}
        </section>
      )}
      {"show_ids" in entity && (
        <div className="entity-shows">
          {catalogue.shows
            .filter((s) => entity.show_ids.includes(s.id))
            .map((s) => (
              <a className="button" href={route("show", s.id)} key={s.id}>
                {s.name}
                <Icon name="arrow" size={16} />
              </a>
            ))}
        </div>
      )}
      <SectionHead label={t("episodes")} count={episodes.length} />
      <EpisodeGrid episodes={episodes} />
    </>
  );
}
type Feed = {
  days: {
    date: string;
    entries: {
      kind: string;
      episode_id: string;
      artist?: string;
      track_title?: string;
      at?: string;
    }[];
  }[];
};
export function WhatsNew() {
  const { catalogue, t, lang, title } = useApp();
  const [sort, setSort] = useState("added"),
    [feed, setFeed] = useState<Feed | null>(null);
  useEffect(() => {
    api
      .whatsNew()
      .then(setFeed)
      .catch(() => {});
  }, []);
  const groups: Record<string, typeof catalogue.episodes> = {};
  [...catalogue.episodes]
    .sort((a, b) =>
      (sort === "added" ? b.imported_at : b.broadcast_date || "").localeCompare(
        sort === "added" ? a.imported_at : a.broadcast_date || "",
      ),
    )
    .forEach((ep) => {
      const d = (
        sort === "added" ? ep.imported_at : ep.broadcast_date || ""
      ).slice(0, 10);
      (groups[d] ||= []).push(ep);
    });
  const recurring = catalogue.artists.filter((a) => a.episode_ids.length > 1);
  const connections = recurring.length
    ? recurring
    : catalogue.artists.filter((a) =>
        a.appearances.some((p) => p.role === "guest"),
      );
  const confirmations =
    feed?.days.flatMap((d) =>
      d.entries.filter((e) => e.kind !== "episode_added"),
    ) || [];
  return (
    <>
      <PageHeader
        eyebrow={t("new")}
        title={t("newTitle")}
        description={t("newIntro")}
      />
      <div className="feed-layout">
        <section>
          <div className="feed-controls">
            <button
              aria-pressed={sort === "added"}
              onClick={() => setSort("added")}
            >
              {t("addedDate")}
            </button>
            <button
              aria-pressed={sort === "broadcast"}
              onClick={() => setSort("broadcast")}
            >
              {t("broadcast")}
            </button>
          </div>
          {Object.entries(groups).map(([d, eps]) => (
            <section className="feed-day" key={d}>
              <h2>
                <span className="live-dot" />
                {date(d, lang)}
              </h2>
              <div>
                {eps.map((ep) => (
                  <article className="feed-entry" key={ep.id}>
                    <a
                      href={route("episode", ep.id)}
                      tabIndex={-1}
                      aria-hidden="true"
                    >
                      <Artwork episode={ep} />
                    </a>
                    <div className="feed-entry-copy">
                      <span className="eyebrow">
                        <a href={route("station", ep.station_id)}>
                          {ep.station_name}
                        </a>{" "}
                        / {clock(ep.recording?.duration_ms)}
                      </span>
                      <h3>
                        <a dir="auto" href={route("episode", ep.id)}>
                          {title(ep)}
                        </a>
                      </h3>
                      <a href={route("show", ep.show_id)}>{ep.show_title}</a>
                      <div className="feed-tags">
                        {ep.languages.map((l) => (
                          <span key={l}>{l.toUpperCase()}</span>
                        ))}
                        {(ep.counts.cues || 0) > 0 && (
                          <span>CC · EN / FR / AR / JA</span>
                        )}
                      </div>
                    </div>
                    <PlayButton episode={ep} />
                  </article>
                ))}
              </div>
            </section>
          ))}
        </section>
        <aside className="connections">
          <span className="eyebrow">
            {t(recurring.length ? "connections" : "artists")}
          </span>
          <h2>{t("artistsTitle")}</h2>
          {connections.slice(0, 8).map((a) => (
            <a className="connection" href={route("artist", a.id)} key={a.id}>
              <span className="connection-initial">{a.name[0]}</span>
              <span>
                <strong>{a.name}</strong>
                <small>
                  {a.episode_ids.length} {t("episodes")}
                </small>
              </span>
              <Icon name="arrow" size={16} />
            </a>
          ))}
          {confirmations.length > 0 && (
            <>
              <SectionHead label={t("confirmed")} />
              {confirmations.map((c, i) => (
                <a
                  className="connection"
                  key={i}
                  href={route("episode", c.episode_id)}
                >
                  {c.artist} — {c.track_title}
                </a>
              ))}
            </>
          )}
        </aside>
      </div>
    </>
  );
}
export function Search({ initialQuery }: { initialQuery: string }) {
  const { t, lang, catalogue } = useApp(),
    p = usePlayer();
  const [q, setQ] = useState(initialQuery),
    [results, setResults] = useState<SearchResult[]>([]),
    [total, setTotal] = useState(0),
    [loading, setLoading] = useState(false),
    [error, setError] = useState(""),
    [offset, setOffset] = useState(0);
  useEffect(() => {
    setQ(initialQuery);
    setOffset(0);
  }, [initialQuery]);
  useEffect(() => {
    setOffset(0);
  }, [lang]);
  useEffect(() => {
    const abort = new AbortController();
    if (q.trim().length < 2) {
      setResults([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    const timer = setTimeout(() => {
      appApi
        .search(q.trim(), lang, offset, abort.signal)
        .then((data) => {
          setResults((old) =>
            offset ? [...old, ...data.results] : data.results,
          );
          setTotal(data.total);
          setLoading(false);
        })
        .catch((e) => {
          if (e.name !== "AbortError") {
            setError(e.message);
            setLoading(false);
          }
        });
    }, 250);
    return () => {
      clearTimeout(timer);
      abort.abort();
    };
  }, [q, lang, offset]);
  function open(result: SearchResult) {
    if (["artist", "show", "station"].includes(result.kind)) {
      location.hash = route(result.kind, result.id);
      return;
    }
    const ep = catalogue.episodes.find((e) => e.id === result.episode_id);
    if (ep) {
      if (result.start_ms !== null) p.load(ep, result.start_ms);
      location.hash = route("episode", ep.id, result.start_ms);
    }
  }
  return (
    <>
      <PageHeader
        eyebrow={t("search")}
        title={t("searchTitle")}
        description={t("searchHint")}
      />
      <form
        className="search-big"
        onSubmit={(e) => {
          e.preventDefault();
          history.replaceState(null, "", "#/search?q=" + encodeURIComponent(q));
        }}
      >
        <Icon name="search" size={28} />
        <input
          type="search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOffset(0);
          }}
          placeholder={t("searchPlaceholder")}
          aria-label={t("search")}
          autoFocus
        />
        <button className="icon-button" aria-label={t("search")}>
          <Icon name="arrow" />
        </button>
      </form>
      {q.length < 2 ? (
        <div className="search-suggestions">
          {["Yuksek", "Silvestrov", "jazz", "music", "musique"].map((s) => (
            <button
              className="button"
              key={s}
              onClick={() => {
                setQ(s);
                setOffset(0);
              }}
            >
              {s}
              <Icon name="arrow" size={14} />
            </button>
          ))}
        </div>
      ) : (
        <>
          <div className="search-status" role="status">
            {loading ? t("loading") : `${total} ${t("results")}`}
          </div>
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          <div className="search-results">
            {results.map((r, i) => (
              <button
                key={r.id + ":" + i}
                className="search-result"
                onClick={() => open(r)}
              >
                <span className="result-kind">
                  {r.kind === "transcript"
                    ? t("transcript")
                    : r.kind === "track"
                      ? t("tracks")
                      : r.kind === "artist"
                        ? t("artists")
                        : r.kind === "show"
                          ? t("shows")
                          : r.kind === "station"
                            ? t("stations")
                            : t("episode")}
                </span>
                <span>
                  <strong dir="auto">{r.title}</strong>
                  {r.text && (
                    <span className="result-excerpt" dir="auto" lang={r.lang}>
                      {r.kind === "track"
                        ? t(
                            r.text === "candidate"
                              ? "candidate"
                              : r.text === "publisher_only"
                                ? "publisher"
                                : r.text === "unknown"
                                  ? "unknown"
                                  : "confirmed",
                          )
                        : r.text}
                    </span>
                  )}
                  {r.display_text && r.display_text !== r.text && (
                    <small dir="auto" lang={r.display_lang}>
                      {r.display_text}
                    </small>
                  )}
                </span>
                <span className="result-time">
                  {r.start_ms !== null ? (
                    clock(r.start_ms)
                  ) : (
                    <Icon name="arrow" />
                  )}
                </span>
              </button>
            ))}
          </div>
          {!loading && !results.length && !error && (
            <Empty>{t("noResults")}</Empty>
          )}
          {results.length < total && (
            <button
              className="button load-more"
              disabled={loading}
              onClick={() => setOffset(results.length)}
            >
              {t("more")}
            </button>
          )}
        </>
      )}
    </>
  );
}
